


















































































































































































import {Component, Mixins} from 'vue-mixin-decorator';
import {BaseComponent} from '@/mixins/BaseComponent';
import {getModule} from 'vuex-module-decorators';
import {Inject} from 'inversify-props';

import {Notify} from '@/utils/Notify';

import {NtpServer} from '@/model/system/time/ntpServer';
import {Timezone} from '@/model/system/time/timezone';

import {ISystemTimeService} from '@/services/system/ITime.service';

import SystemRegionStore from '@/store/system/SystemRegion.store';

import TimezoneEditor from '@/components/System/Region/TimezoneEditor.vue';
import {DateTime} from '@/utils/DateTime';

enum NtpMode {
  Unknown,
  Pool,
  Custom,
}


@Component({
  components: {TimezoneEditor},
})
export default class SystemRegionPage extends Mixins<BaseComponent>(BaseComponent) {
  private DateTime = DateTime;

  @Inject(Symbol.for('ISystemTimeService'))
  private systemTimeService!: ISystemTimeService;

  private regionStore: SystemRegionStore = getModule(SystemRegionStore, this.$store);

  private saving: boolean = false;

  private timezone: Timezone | null = null;

  private refreshingDatetime = false;

  private timeMode = 'time-automatic';
  private datepicker = false;
  private timepicker = false;

  private manualDate = new Date().toISOString().substr(0, 10);
  private manualTime = new Date().toISOString().substr(11, 8);

  private NtpMode = NtpMode;
  private ntpMode: NtpMode = NtpMode.Unknown;

  private ntpPool: string = '';
  private ntpServers: NtpServer[] = [];

  private async mounted() {
    await this.refresh();
  }

  private async refresh() {
    this.setLoading();
    await this.refreshDatetime();
    await this.refreshNtpPoolServers();
    this.setLoaded();
  }

  get localizedDatetime(): string {
    if (!this.regionStore.Timezone || !this.regionStore.Datetime) {
      return '';
    }

    return DateTime.toLocaleDateTimeString(this.regionStore.Datetime, this.regionStore.Timezone.FullName);
  }

  private async save() {
    this.saving = true;
    try {
      await this.saveTimezone();
      await this.saveNtpServers();
      Notify.Success(this.logger, `Regional settings has been saved`);
      this.saving = false;
    } finally {
      this.saving = false;
    }
  }

  private async refreshDatetime() {
    this.refreshingDatetime = true;
    try {
      await this.regionStore.fetchDatetime();
      await this.regionStore.fetchTimezone();
    } catch (error) {
      Notify.Error(this.logger, `Could not get current date and time`, error);
    } finally {
      this.refreshingDatetime = false;
    }
  }

  private async saveTimezone() {

    if (this.timezone === null) {
      this.logger.warn(`Cannot save timezone, undefined`);
      return;
    }

    try {
      await this.systemTimeService.setTimezone(this.timezone);
      this.regionStore.setTimezone(this.timezone);
    } catch (error) {
      Notify.Error(this.logger, `Could not set timezone`, error);
    }
  }

  private async refreshNtpPoolServers() {
    try {
      await this.regionStore.fetchNtpPool();
      this.ntpPool = this.regionStore.NtpPool || '';
    } catch (error) {
      Notify.Error(this.logger, `Could not get current NTP pool address`, error);
    }

    try {
      await this.regionStore.fetchNtpServers();
      this.ntpServers = this.regionStore.NtpServers || [];

      if (this.ntpServers && this.ntpServers.length > 0) {
        this.ntpMode = NtpMode.Custom;
      } else {
        this.ntpMode = NtpMode.Pool;
        this.addNtpServer();
      }
    } catch (error) {
      Notify.Error(this.logger, `Could not get current NTP servers`, error);
    }
  }

  private addNtpServer() {
    if (!this.ntpServers) {
      this.ntpServers = [];
    }

    this.ntpServers.push(new NtpServer());
  }

  private eraseNtpServer(index: number) {
    this.ntpServers.splice(index, 1);
  }

  private async saveNtpServers() {

    const servers: NtpServer[] = [];
    if (this.ntpMode === NtpMode.Custom) {
      for (const server of this.ntpServers) {
        if (server.Address !== '') {
          servers.push(server);
        }
      }
    }

    try {
      await this.systemTimeService.setNtpServers(servers);
      this.regionStore.setNtpServers(servers);

      try {
        const output = await this.systemTimeService.restartNtp();
        this.logger.info(`NTP client restarted`, output);
      } catch (error) {
        Notify.Error(this.logger, `The NTP client could not be restarted. The new configuration will be effective only once the NTP client restarted.`, error);
      }
    } catch (error) {
      Notify.Error(this.logger, `Could not save NTP servers`, error);
    }
  }
}
